import React, { useReducer, useEffect } from 'react';

import clsx from 'clsx';
import { concat } from 'lodash';
import PropTypes from 'prop-types';
import KeyStore from 'utils/KeyStore';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import Permission from 'utils/enum/Permissions';
import { ActivityType } from 'utils/enum/ActivitiesEnum';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import { ReactComponent as AddCircleOutlineIcon } from 'assets/addproduct.svg';
import TaskActivityItem from 'components/modules/crm/activities/TaskActivityItem';
import ActivitiesForm from 'components/modules/crm/activities/form/ActivitiesForm';

// Materia UI
import { makeStyles, IconButton } from '@material-ui/core';

// Apollo
import { useQuery, useSubscription } from '@apollo/client';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import ActivitiesQuery from 'services/graphQL/query/crm/ActivitiesQuery';
import ActivitySubscription from 'services/graphQL/subscription/crm/ActivitySubscription';
import { FetchPolicy } from 'utils/enum/Core';

const useStyles = makeStyles((theme) => ({
    body: {
        height: '100%',
        overflow: 'auto',
        overflowX: 'hidden',
    },
    header: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(1),
    },
    container: {
        height: '100%',
        padding: theme.spacing(2),
    },
}));

const ACTION_TYPE = {
    SET_OFFSET: 'setOffset',
    ADD_RECORD: 'addRecord',
    SET_RECORDS: 'setRecords',
    TOGGLE_MODAL: 'toggleModal',
    UPDATE_RECORD: 'updateRecord',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.TOGGLE_MODAL:
        return update(state, {
            open: { $set: action.open },
            isEditing: { $set: action.isEditing },
            activityId: { $set: action.activityId },
        });
    case ACTION_TYPE.SET_RECORDS:
        const records = concat(state.records, action.payload?.data);
        return update(state, {
            records: { $set: records },
            totalCount: { $set: action.payload?.totalCount },
        });
    case ACTION_TYPE.UPDATE_RECORD:
        const index = state.records.findIndex((item) => item.activityId === action?.payload?.data?.activityId);
        if (index >= 0) {
            return update(state, {
                records: { [index]: { $set: action?.payload?.data || {} } },
            });
        }
        return state;
    case ACTION_TYPE.ADD_RECORD:
        return update(state, {
            records: { $unshift: [action?.payload?.data] },
            totalCount: { $set: state.totalCount + 1 },
        });
    case ACTION_TYPE.SET_OFFSET:
        return update(state, {
            offset: { $set: action.value },
        });
    default:
        return state;
    }
};

const TaskActivity = ({ crmId, lotId }) => {
    const classes = useStyles();
    const keyStore = new KeyStore();
    const [state, dispatch] = useReducer(reducer, {
        open: false,
        records: [],
        totalCount: 0,
        offset: 0,
    });
    const {
        data, error, loading,
    } = useQuery(ActivitiesQuery.GET_TASK_ACTIVITIES_BY_CRMID,
        {
            variables: { crmId, paging: { start: state.offset, limit: 25 } },
            fetchPolicy: FetchPolicy.NETWORK_ONLY,
        });

    const { data: subscriptionData } = useSubscription(ActivitySubscription.UPDATED_ACTIVITY_TASK, {
        variables: {
            crmId,
        },
        shouldResubscribe: true,
    });
    const CRM_LEAD_ACTIVITY_WRITE = keyStore.hasPermission(Permission.CRM_LEAD_ACTIVITY_WRITE);

    useEffect(() => {
        if (!loading) {
            dispatch({
                type: ACTION_TYPE.SET_RECORDS,
                payload: data?.getTaskActivitiesByCRMId,
            });
        }
    }, [data, loading]);

    useEffect(() => {
        if (subscriptionData) {
            if (subscriptionData.updatedActivityTask.type === SubscriptionActionType.ADDED) {
                dispatch({
                    type: ACTION_TYPE.ADD_RECORD,
                    payload: subscriptionData.updatedActivityTask,
                });
            }

            if (subscriptionData.updatedActivityTask.type === SubscriptionActionType.UPDATED) {
                dispatch({
                    type: ACTION_TYPE.UPDATE_RECORD,
                    payload: subscriptionData.updatedActivityTask,
                });
            }
        }
    }, [subscriptionData]);

    const onClose = () => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_MODAL,
            open: false,
            isEditing: false,
            activityId: null,
        });
    };

    const onOpen = (record, isEditing = false) => {
        dispatch({
            type: ACTION_TYPE.TOGGLE_MODAL,
            open: true,
            isEditing,
            activityId: record.activityId,
        });
    };

    const loadMore = () => {
        const currentOffset = state.records.length;
        dispatch({ type: ACTION_TYPE.SET_OFFSET, value: currentOffset });
    };

    if (error) {
        ModalUtils.errorMessage(error.graphQLErrors);
        return null;
    }

    return (
        <div className={clsx('d-flex-column', classes.container)}>
            {CRM_LEAD_ACTIVITY_WRITE && (
                <div className={classes.header}>
                    <IconButton
                        size="small"
                        aria-label="Add"
                        onClick={onOpen}
                    >
                        <AddCircleOutlineIcon />
                    </IconButton>
                </div>
            )}
            <InfiniteScroll
                lengthRecord={state?.records.length}
                totalRecord={state?.totalCount}
                loadMore={loadMore}
                load={loading}
                loadAtScrollPercent={80}
            >
                {
                    state.records.map((item, index) => (
                        <TaskActivityItem
                            record={item}
                            key={index}
                            onOpen={onOpen}
                        />
                    ))
                }
            </InfiniteScroll>
            {state.open && (
                <ActivitiesForm
                    crmId={crmId}
                    lotId={lotId}
                    open={state.open}
                    onClose={onClose}
                    type={ActivityType.TASK}
                    isEditing={state.isEditing}
                    activityId={state.activityId}
                />
            )}
        </div>
    );
};

TaskActivity.propTypes = {
    crmId: PropTypes.string.isRequired,
    lotId: PropTypes.number,
};

TaskActivity.defaultProps = {
    lotId: null,
};

export default TaskActivity;
